/*
 *  -----------------------------------------------------------------------
 *    <copyright file="child-actions-grid-v2.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { UseBoolean } from 'react-hanger';
import { useNavigate } from 'react-router';
import { orderBy } from '@progress/kendo-data-query';
import ActionsEmailDialog from '@components/central-monitoring/actions-email-dialog/actions-email-dialog';
import { ICMActionFieldsExtended } from '@pages/central-monitoring/manage-actions.interface';
import { ICMObservationAndActionExtended } from '@pages/central-monitoring/manage-observations.interface';
import { IActionItemV2 } from '../core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '../core/grid-filters';
import { ExtraMenuItemPosition } from '../core/grid-header/grid-menu/grid-menu.interface';
import AddAction from './add-action';
import { ActRowAction } from './observations-grid-v2.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useBoolean, useTranslation, useGridCells } from 'hooks';
import { RiskObservationFields_GridList } from 'interfaces/risk-observation.interface';
import { DashboardType } from 'types/graphql-global-types';
import { handleTimeInDates, reorderArray, showMaxLength } from 'utils/filter';
import NotificationService from 'utils/notification';
import ActionDialog from 'views/components/core/action-dialog';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2';
import InformationDialog from 'views/components/core/information-dialog/information-dialog';
import { AllRoutes } from '@utils/router/all-routes';
import {
    StudyFields_Study_members_StudyMember,
    StudyFields,
    CompleteRiskFields,
    CentralMonitoringActionFields,
} from '@queries/generated/fragments.generated';
// eslint-disable-next-line max-len
import { riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction } from '@queries/generated/legacy-risk-assessment.generated';

interface ChildActionsGridV2Props {
    study: StudyFields;

    observation: RiskObservationFields_GridList | ICMObservationAndActionExtended;
    showDeletedItems?: UseBoolean;
    title?: string;
    detailClass?: string;
    canEdit?: boolean;
    isCMR?: boolean;
    risk?: CompleteRiskFields;
    type?: string;
    handleRestoreAct?: Function;
    handleDeleteAct?: Function;
    onClose?: () => void;
}

const ChildActionsGridV2 = ({
    title,
    study,
    risk,
    observation,
    detailClass,
    canEdit,
    isCMR,
    type,
    showDeletedItems,
    handleDeleteAct,
    handleRestoreAct,
    onClose,
}: ChildActionsGridV2Props) => {
    const { DateCell, MaxLengthCell, NumberCell, ActiveInactiveCell, YesNoCell } = useGridCells();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Used by max length cells to prevent clipping out of the page
    const tooltipHeight = '30vh';

    const { actions } = observation;

    const owners =
        study?.members?.map((member: StudyFields_Study_members_StudyMember) => ({
            id: member.id,
            text: member.text ?? `${member.firstName} ${member.lastName}`,
            active: member.active,
        })) || [];

    const showInformationDialog = useBoolean(false);
    const showEmailDialog = useBoolean(false);
    const showAddDialog = useBoolean(false);
    const showRestoreActionWarning = useBoolean(false);

    const [data, setData] = useState(null);
    const [informationDialogText, setInformationDialogText] = useState(null);
    const [currentActions, setCurrentActions] = useState([]);
    const [currentAction, setCurrentAction] = useState(null as ICMActionFieldsExtended);

    useEffect(() => {
        let array = [...actions];
        // we need to have this logic when we have two fields for sorting which are dates or when the first sorting field is date
        let arrayToSort = array.slice();
        arrayToSort = handleTimeInDates(arrayToSort, ['completedDate', 'updated']);
        arrayToSort =
            orderBy(arrayToSort, [
                { field: 'completedDate', dir: 'asc' },
                { field: 'updated', dir: 'desc' },
            ]) ?? [];
        array = reorderArray(array, arrayToSort);
        // end of logic
        const newData = restructuredUpdatedCreatedBy(array);
        setData(newData);
    }, [actions]);

    const onActionsEmailDialogClose = (action: string) => {
        showEmailDialog.setFalse();
        if (action === 'done') NotificationService.showNotification(t('ra:successfulSentEmail'));
        if (action === 'error') NotificationService.showNotification(t('errorMessage'));
        setData(data.map((item: any) => ({ ...item, checked: false })));
    };

    const handleEditDialogOpen = (item: CentralMonitoringActionFields) => {
        setCurrentAction(item);
        showAddDialog.setTrue();
    };

    const handleEditDialogClose = () => {
        showAddDialog.setFalse();
    };

    const validateRestore = (item: CentralMonitoringActionFields) => {
        if (observation.closedDate && !item.completedDate) {
            showRestoreActionWarning.setTrue();
        } else {
            handleRestoreAct(item);
        }
    };

    const rowActions: ActRowAction = (item) => {
        if (item?.deleted) {
            return [
                {
                    label: 'cm:restoreAction',
                    icon: ['fal', 'undo'],
                    onClick: () => validateRestore(item),
                    enabled: canEdit && !isCMR,
                },
            ];
        }

        const availableActions: IActionItemV2[] = [
            {
                label: 'ra:editAction',
                icon: ['fal', 'edit'],
                onClick: () => handleEditDialogOpen(item),
                enabled: canEdit && !isCMR,
            },
            {
                label: 'cm:deleteAction',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteAct(item),
                enabled: canEdit && !isCMR,
            },
        ];

        if (type === DashboardType.SITE) {
            availableActions.push({
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    if (onClose !== undefined) {
                        onClose();
                    }
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: item._siteId as string | null | undefined,
                        },
                    });
                },
                enabled: item._siteStatus?.length
                    ? (study.sitesStatusesToExclude?.indexOf(item._siteStatus) < 0 ?? true)
                    : false,
            });
        }

        return availableActions;
    };

    const canOpen = (
        checkedRows: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
    ) => {
        if (!checkedRows) {
            return false;
        }
        return checkedRows.every(
            (dataItem: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction) =>
                dataItem.ownerId === checkedRows[0].ownerId,
        );
    };

    const handleSelection = (
        oldChecked: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
        newChecked: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
    ): boolean => {
        if (newChecked.some((dataItem) => !dataItem.owner?.email)) {
            setInformationDialogText(t('ra:ownerHasNotEmail'));
            showInformationDialog.setTrue();
            return false;
        }
        if (newChecked.some((dataItem) => !dataItem.owner?.active)) {
            setInformationDialogText(t('ra:ownerIsNotActive'));
            showInformationDialog.setTrue();
            return false;
        }
        if (!canOpen(newChecked)) {
            setInformationDialogText(t('ra:youCanChooseActionOnlySameOwner'));
            showInformationDialog.setTrue();
            return false;
        }
        return true;
    };

    return (
        <>
            {showRestoreActionWarning.value && (
                <ActionDialog
                    title={t('cm:restoreAction')}
                    onClose={() => showRestoreActionWarning.setFalse()}
                    asNotification
                    className="action-dialog-small"
                >
                    <div>{t('cm:restoreActionWarning')}</div>
                </ActionDialog>
            )}
            <InformationDialog
                open={showInformationDialog.value}
                onClose={() => showInformationDialog.setFalse()}
                text={informationDialogText}
                heading={t('ra:actionEmail')}
            />
            <ActionsEmailDialog
                study={study}
                risks={[risk]}
                actions={currentActions}
                observations={[observation]}
                open={showEmailDialog.value}
                close={onActionsEmailDialogClose}
                query="SEND_CM_ACTION_EMAIL"
                canEdit={canEdit}
            />
            <AddAction
                study={study}
                observationId={observation?.id}
                owners={owners}
                open={showAddDialog.value}
                currentAction={currentAction}
                canEdit={canEdit}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else if (action === 'edit') {
                        NotificationService.showNotification(
                            `${t('subject:action')} ${currentAction.recordId} ${t('ra:successfulUpdated')}`,
                        );
                    }
                    setCurrentAction(null);
                    handleEditDialogClose();
                }}
            />
            <div className="child-grid-wrap" key="control-ismp-grid-wrapper">
                <ExpandableGridV2
                    handleSelection={handleSelection}
                    title={title}
                    isChildGrid
                    customKey="child-grid-action"
                    id="childGridAction"
                    items={data}
                    groupable={false}
                    showPager
                    hideExpand
                    scrollable="scrollable"
                    reorderable
                    resizable
                    pageable={false}
                    detailClass={detailClass}
                    extraMenuItems={
                        canEdit
                            ? [
                                  {
                                      position: ExtraMenuItemPosition.expandMenu,
                                      expandMenuItems: [
                                          {
                                              label: 'uam:emailSelected',
                                              icon: ['fal', 'envelope'],
                                              requireSelection: true,
                                              enabled: (checkedRows: any) => !!checkedRows.length,
                                              onClick: (checkedRows: any) => {
                                                  setCurrentActions(checkedRows);
                                                  showEmailDialog.setTrue();
                                              },
                                              testId: 'email-selected',
                                          },
                                      ],
                                  },
                              ]
                            : []
                    }
                    showCheckbox={canEdit}
                    rowActions={rowActions}
                    hideDeletedToggle
                    showDeletedItems={showDeletedItems}
                    columns={[
                        {
                            field: 'deleted',
                            id: 'action-deleted',
                            label: t('deleted'),
                            minWidth: 134,
                            showByDefault: false,
                            cells: { data: YesNoCell },
                            filterUi: GridColumnMenuActiveFilter,
                        },
                        {
                            label: t('site'),
                            field: '_site',
                            minWidth: 108,
                            lockedByDefault: true,
                            id: 'action-site',
                            cells: { data: MaxLengthCell(50) },
                        },
                        {
                            label: t('ra:description'),
                            field: 'description',
                            minWidth: 174,
                            lockedByDefault: true,
                            id: 'action-description',
                            cells: { data: MaxLengthCell(50, tooltipHeight) },
                        },
                        {
                            field: 'requestDate',
                            label: t('ra:requestedDate'),
                            id: 'action-requestDate',
                            filter: 'date',
                            minWidth: 156,
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            label: t('cm:actionTaken'),
                            field: 'actionTaken',
                            id: 'risk-ma-actionTaken',
                            minWidth: 174,
                            cells: { data: MaxLengthCell(50, tooltipHeight) },
                        },
                        {
                            label: t('cm:rootCause'),
                            field: 'rootCause',
                            id: 'risk-ma-rootCause',
                            minWidth: 174,
                            cells: { data: MaxLengthCell(50, tooltipHeight) },
                        },
                        {
                            field: 'result',
                            label: t('ra:result'),
                            id: 'action-result',
                            minWidth: 174,
                            cells: { data: MaxLengthCell(50, tooltipHeight) },
                        },
                        {
                            field: 'expectedCompleteDate',
                            label: t('ra:expectedCompletionDate'),
                            filter: 'date',
                            id: 'action-expected-complete-date',
                            minWidth: 200,
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            field: 'completedDate',
                            label: t('ra:completedDate'),
                            filter: 'date',
                            id: 'action-complete-date',
                            minWidth: 158,
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            field: 'ownerLabel',
                            label: t('owner'),
                            id: 'action-owner',
                            minWidth: 126,
                            cells: {
                                data: ActiveInactiveCell(
                                    (dataItem: any) => dataItem._owner?.active === false,
                                    (dataItem: any) => showMaxLength(dataItem.ownerLabel, 50),
                                ),
                            },
                        },
                        {
                            field: '_updatedByString',
                            label: t('ra:updatedBy'),
                            id: 'action-updated-by',
                            minWidth: 140,
                        },
                        {
                            field: 'updated',
                            label: t('ra:updatedDate'),
                            filter: 'date',
                            minWidth: 144,
                            id: 'action-updated',
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            field: '_deleteReason',
                            id: 'action-delete-reason',
                            label: t('cm:deleteReasonLabel'),
                            showByDefault: false,
                            minWidth: 174,
                            cells: { data: MaxLengthCell(50, tooltipHeight) },
                        },
                        {
                            field: '_deleteDate',
                            id: 'action-delete-date',
                            label: t('cm:deleteDate'),
                            showByDefault: false,
                            filter: 'date',
                            minWidth: 138,
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            field: '_deletedBy',
                            id: 'action-deleted-by',
                            label: t('cm:deletedBy'),
                            showByDefault: false,
                            minWidth: 138,
                        },
                        {
                            field: 'created',
                            label: t('ra:createdDate'),
                            minWidth: 180,
                            filter: 'date',
                            id: 'action-created',
                            showByDefault: false,
                            cells: { data: DateCell },
                            filterUi: GridColumnDateFilter,
                        },
                        {
                            field: '_createdByString',
                            label: t('ra:createdBy'),
                            id: 'action-created-by',
                            minWidth: 180,
                            showByDefault: false,
                        },
                        {
                            field: 'recordId',
                            showByDefault: false,
                            label: t('ra:id'),
                            filter: 'numeric',
                            minWidth: 120,
                            id: 'action-id',
                            cells: { data: NumberCell },
                        },
                    ]}
                />
            </div>
        </>
    );
};

export default ChildActionsGridV2;
